import { Box, Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  Columns,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  Search,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import {
  DueDiligenceForGroupRowResultDataType,
  formatCategoryTableModalData,
  getConfidenceScoreColor,
} from '../../../core-utils/helper-utils/recommendedAppsHelper';
import clsx from 'clsx';
import { RECOMMENDED_APPS } from '../../../core-utils/messages/RecommendedAppConstants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CategoryTable from '../../atoms/CategoryTable';
import { cellStyles4 } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';

export type MADueDiligenceModalProps = {
  parameter: string;
  subParameter: string;
  question: string;
  confidenceScore: string;
  answer: string;
  rationale: string;
  result: DueDiligenceForGroupRowResultDataType[];
};

const parameterTableColumns: Columns[] = [
  {
    name: 'Sub-Parameter',
    align: 'left',
    cellStyles: cellStyles4,
    width: '130px',
  },
  {
    name: 'Question',
    align: 'left',
    cellStyles: cellStyles4,
    width: '250px',
  },
  {
    name: 'Parameter',
    align: 'left',
    cellStyles: cellStyles4,
    width: '130px',
  },
];

const informationSourcesColumns: Columns[] = [
  {
    name: 'Information Extracted',
    align: 'left',
    cellStyles: cellStyles4,
    width: '20%',
  },
  {
    name: 'Document Source/ Section',
    align: 'left',
    cellStyles: cellStyles4,
    width: '25%',
  },
  {
    name: 'Evidence Excerpt',
    align: 'left',
    cellStyles: cellStyles4,
    width: '25%',
  },
  {
    name: 'Score Rationale',
    align: 'left',
    cellStyles: cellStyles4,
    width: '30%',
  },
];
const MADueDiligenceModal: React.FC<MADueDiligenceModalProps> = ({
  subParameter,
  question,
  parameter,
  confidenceScore,
  answer,
  rationale,
  result,
}) => {
  const { classes } = makeStyles()({
    tableTitle: {
      backgroundColor: patientAccessTheme.palette.grey[400],
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 18px 8px 20px',
      gap: '0.5rem',
    },
    result: {
      backgroundColor: patientAccessTheme.palette.navigationBar?.collapseIconBg,
    },
    rationale: {
      backgroundColor: patientAccessTheme.palette.grey[700],
    },
    labelColor: {
      height: '1rem',
      width: '1rem',
      borderRadius: '50%',
    },
    lableValueContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      padding: '0.25rem',
      borderRadius: '0.25rem',
      marginLeft: '0.5rem',
    },
    tooltip: {
      backgroundColor: patientAccessTheme.palette.grey[900],
      padding: '10px',
      fontSize: '13px',
      fontFamily: 'Inter',
      fontWeight: 400,
      lineHeight: '16px',
      color: patientAccessTheme.palette.status.white,
    },
    arrow: {
      color: patientAccessTheme.palette.grey[900],
      marginLeft: '50px',
    },
    popper: {
      marginTop: '-8px',
      marginLeft: '-34px',
    },
    ellipsis: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      color: patientAccessTheme.palette.text.mediumemphasis,
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
    },
  })();

  const messages = RECOMMENDED_APPS.MA_DUE_DILIGENCE;
  return (
    <Grid
      container
      spacing={2}
      style={{ paddingRight: '0.5rem' }}
      sx={{
        flexDirection: 'column',
      }}
    >
      <Grid>
        <Box className={classes.tableTitle}>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            Parameter
          </Typography>
        </Box>
        <BasicTable
          columns={parameterTableColumns}
          rowsData={[[subParameter, question, parameter]]}
          displayType="box"
          headerTextColor={patientAccessTheme.palette.status.white}
          headerColor={patientAccessTheme.palette.grey[700]}
          headerCellHeight="34px"
          cellHeight="34px"
          borderRadius="0px"
          isFullTable
        />
      </Grid>
      <Grid>
        <Box className={classes.tableTitle}>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
            styleProps={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            Information Extracted <Search width="18px" height="18px" />
          </Typography>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            Confidence Score:{' '}
            <span
              style={{
                color: getConfidenceScoreColor(confidenceScore.toLowerCase()),
              }}
            >
              {confidenceScore.toUpperCase()}
            </span>
          </Typography>
        </Box>
        <Box className={clsx(classes.tableTitle, classes.result)}>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.highemphasis}
          >
            {answer}
          </Typography>
        </Box>
        <Box className={clsx(classes.tableTitle, classes.rationale)}>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.status.white}
          >
            Rationale
          </Typography>
        </Box>
        <Box className={clsx(classes.tableTitle, classes.result)}>
          <Typography
            fontSize="14px"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
          >
            {rationale}
          </Typography>
        </Box>
      </Grid>
      <Grid
        container
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          fontSize="14px"
          fontType="medium"
          textColor={patientAccessTheme.palette.text.mediumemphasis}
        >
          Confidence Score:{' '}
        </Typography>

        <Box className={classes.lableValueContainer}>
          {messages.CONFIDENCE_SCORE_LIST.map((item) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <div
                className={classes.labelColor}
                style={{ backgroundColor: item.color }}
              ></div>
              <Typography
                fontSize="14px"
                fontType="medium"
                lineHeight="28px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                {item?.name}
              </Typography>
              <Tooltip
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                  popper: classes.popper,
                }}
                title={item?.tooltip}
                placement="bottom-start"
              >
                <InfoOutlinedIcon
                  style={{
                    width: '18px',
                    height: '18px',
                    fill: patientAccessTheme.palette.status.icon,
                  }}
                />
              </Tooltip>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid>
        <Box className={classes.tableTitle}>
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
            styleProps={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            Information Sources <Search width="18px" height="18px" />
          </Typography>
        </Box>
        <CategoryTable
          columns={informationSourcesColumns}
          rowsData={formatCategoryTableModalData(result)}
          isCategory={false}
          borderRadius="0px"
          padding="8px 8px 8px 20px"
        />
      </Grid>
    </Grid>
  );
};

export default MADueDiligenceModal;
